import { useCallback, useEffect, useState } from "react"
import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"

import CustomRichText from "~/components/CustomRichText"
import LazyImage from "~/components/LazyImage"
import { imageOrFallback } from "~/utils/images/imageOrFallback"
import * as RichText from "~/utils/prismic/richText"

import type { ImageGalleryComponent } from "./types"

const CarouselGallery: ImageGalleryComponent = ({ slice }) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [viewportRef, embla] = useEmblaCarousel({
    loop: true,
    skipSnaps: false,
  })
  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
  })

  const onThumbClick = useCallback(
    (index: number) => {
      if (!embla || !emblaThumbs) return
      if (emblaThumbs.clickAllowed()) embla.scrollTo(index)
    },
    [embla, emblaThumbs],
  )

  const onSelect = useCallback(() => {
    if (!embla || !emblaThumbs) return
    setSelectedIndex(embla.selectedScrollSnap())
    emblaThumbs.scrollTo(embla.selectedScrollSnap())
  }, [embla, emblaThumbs, setSelectedIndex])

  useEffect(() => {
    if (!embla) return
    onSelect()
    embla.on("select", onSelect)

    return () => {
      embla.off("select", onSelect)
    }
  }, [embla, onSelect])

  const titleText = RichText.asText(slice.primary.gallery_name)
  const images = slice.items.map((img) => ({
    image: img.gallery_image,
    captions: img.image_captions,
  }))
  const selectedImage = images[selectedIndex % images.length]

  return (
    <>
      {!!titleText && (
        <div className="px-4">
          <CustomRichText field={slice.primary.gallery_name} />
        </div>
      )}

      <div className={clsx("embla", "overflow-hidden")} ref={viewportRef}>
        <div className={clsx("embla__container", "flex")}>
          {images.map((_, index) => {
            const { image } = images[index % images.length]

            return (
              <div
                key={`${index}-${image.url}`}
                className={clsx(
                  "embla__slide",
                  "relative w-full shrink-0 grow-0",
                )}
              >
                <LazyImage
                  className={clsx(
                    "embla__slide__img",
                    "!m-0 min-h-full w-full",
                  )}
                  src={imageOrFallback(image.url)}
                  alt={image.alt || ""}
                  lazySize={{ h: 10 }}
                />
              </div>
            )
          })}
        </div>
      </div>

      <p className="my-2 mx-auto px-4 text-center text-sm italic">
        {RichText.asText(selectedImage?.captions)}
      </p>

      <div className="mt-4 flex justify-center px-4">
        <div
          className={clsx("embla", "overflow-hidden")}
          ref={thumbViewportRef}
        >
          <div className="embla__container flex space-x-2">
            {images.map(({ image }, i) => {
              return (
                <button
                  className="embla__slide"
                  key={image.url}
                  onClick={() => onThumbClick(i)}
                  type="button"
                >
                  <LazyImage
                    className={clsx("!m-0 h-14 w-14", {
                      "opacity-50": i !== selectedIndex,
                    })}
                    src={imageOrFallback(image.url)}
                    alt={image.alt || ""}
                  />
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}

export default CarouselGallery
